import { useCallback, useEffect, useRef, useState } from 'react'
import { onAppEvent } from '@/src/renderer/lib/events'
import type { Task } from '@/src/renderer/lib/db/models'

export interface QuickNotesClarifyApi {
  clarifyMode: boolean
  clarifyTasks: Task[] | null
  enterClarify: () => void
  exitClarify: () => void
  toggleClarify: () => void
}

/**
 * Owns the "Einträge klären" (Clarify) mode for Quick Notes: a modal-ish
 * secondary view that operates on a snapshot of the current task list.
 *
 * The snapshot is captured at the moment the user enters clarify mode so the
 * view is stable even while new tasks arrive via live queries.
 *
 * @param lifeSpace - current life space; if it changes while clarify is open
 *   the snapshot would belong to the previous space's project, so we reset.
 * @param tasks - live task list; a clarify snapshot is taken from this at enter time.
 */
export function useQuickNotesClarify(
  lifeSpace: string,
  tasks: Task[] | undefined,
): QuickNotesClarifyApi {
  const [clarifyMode, setClarifyMode] = useState(false)
  const snapshotRef = useRef<Task[] | null>(null)

  const enterClarify = useCallback(() => {
    if (!tasks || tasks.length === 0) return
    snapshotRef.current = tasks
    setClarifyMode(true)
  }, [tasks])

  const exitClarify = useCallback(() => {
    snapshotRef.current = null
    setClarifyMode(false)
  }, [])

  const toggleClarify = useCallback(() => {
    if (clarifyMode) exitClarify()
    else enterClarify()
  }, [clarifyMode, enterClarify, exitClarify])

  // Clicking "Kurz notiert" in the sidebar means "show me the plain list",
  // so it leaves Einsortieren mode. Same class of concern as the life-space
  // reset below: an outside signal invalidates the clarify session.
  useEffect(() => onAppEvent('quick-notes-exit-clarify', (space) => {
    if (space === lifeSpace) exitClarify()
  }), [lifeSpace, exitClarify])

  // Invalidate the snapshot whenever the user switches life space. The
  // snapshot was taken from the previous space's project and would keep the
  // ClarifyView rendering stale content (only --space tokens update on the
  // life-space switch, not React state).
  const prevLifeSpaceRef = useRef(lifeSpace)
  useEffect(() => {
    if (prevLifeSpaceRef.current === lifeSpace) return
    prevLifeSpaceRef.current = lifeSpace
    snapshotRef.current = null
    setClarifyMode(false)
  }, [lifeSpace])

  return {
    clarifyMode,
    clarifyTasks: snapshotRef.current,
    enterClarify,
    exitClarify,
    toggleClarify,
  }
}
